import React, { useCallback, useEffect, useState } from "react";
import Page from "../../types/Page";
import Layout from "../../components/Layout";
import {
  Button,
  Card,
  Col,
  Collapse,
  Form,
  FormControl,
  FormLabel,
  Modal,
  Nav,
  Row,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import PageHeader from "../../components/PageHeader";
import CabinCard from "../../components/CabinCard";
import { Rating } from "@lomarengas/design-system";
import AccommodationBasic from "./AccommodationBasic";
import AccommodationModal from "./AccommodationModal";
import AccommodationDesc from "./AccommodationDesc";
import AccommodationServices from "./AccommodationServices";
import AccommodationAddons from "./AccommodationAddons";
import AccommodationImages from "./AccommodationImages";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearInputErrors,
  selectInputErrors,
} from "../../features/errors/errorsSlice";
import { useAuthUser } from "../../features/auth/authHooks";
import { LOADING_STATUS } from "../../common/constants";
import {
  getAccommodationsAsync,
  selectAccommodationItems,
  selectAccommodationsLoadingStatus,
} from "./accommodationsSlice";
import {
  AccommodationDetailsBasic,
  AccommodationGeoCode,
  AccommodationImage,
  getAccommodationDetailsBasicAsync,
  getAccommodationImagesAsync,
  selectAccommodationDetailsBasic,
  selectAccommodationDetailsBasicLoadingStatus,
  selectAccommodationImages,
  selectAccommodationImagesLoadingStatus,
  UpdateAccommodationGeoCodeDto,
  updateAccommodationKeyBoxAsync,
  UpdateAccommodationKeyBoxDto,
  updateAccommodationGeoCodeAsync,
} from "./accommodationSlice";
import { useParams } from "react-router-dom";
import { getAccommodationTranslationItem } from "../../common/misc";
import {
  getIdentityAddressDetailsAsync,
  selectAccommodationAddress,
  selectAccommodationAddressLoadingStatus,
} from "../../features/identity/identitySlice";
import { updateOwnerAsync } from "../UserPage/userSlice";
import { addStatusToast } from "../../components/StatusToast";
import AccommodationDirectLinks from "./AccommodationDirectLinks";

const AccommodationPage = () => {
  const params = useParams();
  const { isOwnerUser } = useAuthUser();
  const [selectedTabKey, setSelectedTabKey] = useState("perustiedot");
  const inputErrors = useAppSelector(selectInputErrors);
  const accommodationDetailsBasic = useAppSelector(
    selectAccommodationDetailsBasic,
  );
  const accommodationImages = useAppSelector(selectAccommodationImages);
  const [submitted, setSubmitted] = useState(false);
  const [errorInKeyBoxSave, setErrorInKeyBoxSave] = useState(false);
  const accommodationDetailsBasicLoadingStatus = useAppSelector(
    selectAccommodationDetailsBasicLoadingStatus,
  );
  const accommodationImagesLoadingStatus = useAppSelector(
    selectAccommodationImagesLoadingStatus,
  );
  const accommodationAddressLoadingStatus = useAppSelector(
    selectAccommodationAddressLoadingStatus,
  );
  const accommodationAddress = useAppSelector(selectAccommodationAddress);
  const dispatch = useAppDispatch();
  const { ownerAccountId } = useAuthUser();

  const initFetch = useCallback(() => {
    dispatch(getAccommodationDetailsBasicAsync(params.accommodationId));
    dispatch(getAccommodationImagesAsync(params.accommodationId));
  }, [dispatch, params.accommodationId]);
  useEffect(() => {
    initFetch();
  }, [initFetch, params.accommodationId]);
  useEffect(() => {
    if (accommodationDetailsBasic != null) {
      dispatch(
        getIdentityAddressDetailsAsync(accommodationDetailsBasic.addressId),
      );
    }
  }, [accommodationDetailsBasic]);

  const handleKeyBoxSave = (keyBox: string) => {
    let updateAccommodationKeyBoxDto: UpdateAccommodationKeyBoxDto = new (class
      implements UpdateAccommodationKeyBoxDto
    {
      accommodationId: string;
      keyBoxCode: string;
    })();
    updateAccommodationKeyBoxDto.accommodationId = params.accommodationId;
    updateAccommodationKeyBoxDto.keyBoxCode = keyBox;
    setSubmitted(false);
    dispatch(updateAccommodationKeyBoxAsync(updateAccommodationKeyBoxDto)).then(
      (res) => {
        if (res.meta.requestStatus === "fulfilled") {
          dispatch(
            addStatusToast({
              Message: `Tiedot tallennettu!`,
            }),
          );
          setSubmitted(true);
          dispatch(clearInputErrors());
        } else {
          setErrorInKeyBoxSave(true);
          //alert(res.payload.message);
          dispatch(
            addStatusToast({
              Title: "Virhe tallennuksessa",
              IsFailed: true,
              Message: res.payload.message,
            }),
          );
          setSubmitted(true);
          dispatch(clearInputErrors());
        }
      },
    );
  };

  let cabinName = getAccommodationTranslationItem(
    "fi_FI",
    "AccommodationName",
    accommodationDetailsBasic,
  );
  if (
    accommodationDetailsBasicLoadingStatus == LOADING_STATUS.LOADING ||
    accommodationDetailsBasic == undefined
  ) {
    cabinName = "";
  }

  const AccommodationPageType: Page = {
    ActiveTab: "Accommodations",
    DisplayName: cabinName,
    DisplayParentName: "Kohteet / Muokkaa",
    LinkUrl: "/accommodations",
    Path: "/accommodations",
    RouteName: "accommodations",
  };

  const handleGeoCodeSave = (lat: string, lng: string) => {
    let updateAccommodationGeoCodeDto: UpdateAccommodationGeoCodeDto =
      new (class implements UpdateAccommodationGeoCodeDto {
        accommodationId: string;
        geoCode: AccommodationGeoCode;
      })();
    updateAccommodationGeoCodeDto.accommodationId = params.accommodationId;
    let updateAccommodationGeoCode: AccommodationGeoCode = new (class
      implements AccommodationGeoCode
    {
      latitude: number;
      longitude: number;
    })();
    updateAccommodationGeoCode.latitude = Number(lat);
    updateAccommodationGeoCode.longitude = Number(lng);
    updateAccommodationGeoCodeDto.geoCode = updateAccommodationGeoCode;
    setSubmitted(false);
    dispatch(
      updateAccommodationGeoCodeAsync(updateAccommodationGeoCodeDto),
    ).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        dispatch(
          addStatusToast({
            Message: `Tiedot tallennettu!`,
          }),
        );
        setSubmitted(true);
        dispatch(clearInputErrors());
      }
    });
  };

  const renderCabinCard = (
    accommodationDetailsBasic: AccommodationDetailsBasic,
    accommodationImages: AccommodationImage[],
  ) => {
    if (accommodationImages.length > 0) {
      let cabinName = getAccommodationTranslationItem(
        "fi_FI",
        "AccommodationName",
        accommodationDetailsBasic,
      );
      return (
        <CabinCard
          Horizontal
          ImgSrc={accommodationImages[0].fullUrl}
          Title={
            accommodationDetailsBasic.accommodationNumber + ", " + cabinName
          }
          Desc=""
          className="d-none mb-24"
        />
      );
    }
    return <></>;
  };
  if (
    accommodationDetailsBasicLoadingStatus == LOADING_STATUS.LOADING ||
    accommodationDetailsBasic == undefined
  ) {
    return (
      <div className={AccommodationPageType.RouteName}>
        <Layout CurrentPage={AccommodationPageType}>
          <PageHeader
            Title={AccommodationPageType.DisplayName}
            SubTitle={AccommodationPageType.DisplayParentName}
          />
          Ladataan kohdetta... <Spinner animation="border" />
        </Layout>
      </div>
    );
  }
  //console.log(accommodationDetailsBasic.translations);
  return (
    <div className={AccommodationPageType.RouteName}>
      <Layout CurrentPage={AccommodationPageType}>
        <PageHeader
          Title={AccommodationPageType.DisplayName}
          SubTitle={AccommodationPageType.DisplayParentName}
        />
        {renderCabinCard(accommodationDetailsBasic, accommodationImages)}
        <Tabs
          onSelect={(e) => setSelectedTabKey(e)}
          defaultActiveKey="perustiedot"
          id="uncontrolled-tab-example"
        >
          <Tab eventKey="perustiedot" title="Perustiedot">
            <AccommodationBasic
              accommodationDetailsBasic={accommodationDetailsBasic}
              accommodationAddress={accommodationAddress}
              handleKeyBoxSave={handleKeyBoxSave}
              handleGeoCodeSave={handleGeoCodeSave}
              errorInKeyBoxSave={errorInKeyBoxSave}
            />
          </Tab>
          <Tab eventKey="kuvaukset" title="Kuvaukset">
            <AccommodationDesc
              accommodationDetailsBasic={accommodationDetailsBasic}
            />
          </Tab>
          <Tab eventKey="varustelu" title="Varustelu">
            <AccommodationServices
              accommodationDetailsBasic={accommodationDetailsBasic}
            />
          </Tab>
          <Tab eventKey="lisapalvelut" title="Lisäpalvelut">
            <AccommodationAddons
              accommodationDetailsBasic={accommodationDetailsBasic}
            />
          </Tab>
          <Tab eventKey="kuvat" title="Kuvat">
            {selectedTabKey == "kuvat" && (
              <AccommodationImages accommodationImages={accommodationImages} />
            )}
          </Tab>
          {isOwnerUser && (
            <Tab eventKey="suoralinkit" title="Suoralinkit">
              {selectedTabKey == "suoralinkit" && (
                <AccommodationDirectLinks
                  accommodationDetailsBasic={accommodationDetailsBasic}
                />
              )}
            </Tab>
          )}
        </Tabs>
        {isOwnerUser && (
          <AccommodationModal
            accommodationDetailsBasic={accommodationDetailsBasic}
            accommodationImages={accommodationImages}
            renderCabinCard={renderCabinCard}
          />
        )}
      </Layout>
    </div>
  );
};

export default AccommodationPage;
